import { Box, Spinner } from '@chakra-ui/react';

import { isEmpty, isNotDefined } from '@enterprise/core';

import type { HasAccessProps } from './types/has-access.props';

import { useHasAccessConfiguration } from './hooks/has-access-configuration.hook';

/**
 * A component that conditionally renders its children based on access permissions.
 *
 * @remarks
 * Uses the `useHasAccessConfiguration` hook to determine if the user has access.
 * Displays a loading state while access is being determined, the `children` if access is granted,
 * or a denied state if access is denied.
 *
 * @param props - The props for the HasAccess component.
 * @param props.children - The content to render if access is granted.
 * @param props.denied - The content to render if access is denied.
 * @param props.loading - The content to render while loading access state.
 * @returns The appropriate content based on access and loading state.
 */
export const HasAccess = ({
  children,
  denied,
  loading,
  permissions,
  permissionsStrategy,
  roles,
  rolesStrategy,
  ...props
}: HasAccessProps) => {
  const { hasAccess, isError, isLoading } = useHasAccessConfiguration({
    permissions,
    permissionsStrategy,
    roles,
    rolesStrategy,
  });

  const content = isLoading ? (loading ?? <Spinner />) : hasAccess && !isError ? children : denied;

  if (isNotDefined(content)) {
    return null;
  }

  if (isNotDefined(props) || isEmpty(props)) {
    return content;
  }

  return <Box {...props}>{content}</Box>;
};
